import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { ProgressBarKindProps } from '../../types';
import { Text } from '../../../Text';
import { Image } from '../../../Image';
import styles from './LineProgressBar.module.css';

export const LineProgressBar = memo(function LineProgressBar({
  stepper,
  progress,
  locale,
  currentSegment,
  currentSegmentIndex,
  labelStyle,
  hasLabelStyle,
  containerStyle,
  variables,
  previewMode,
  className,
  testId,
  editPath,
}: ProgressBarKindProps): React.ReactElement {
  const { appearance } = stepper;

  const barHeight = appearance?.thickness ?? 8;
  const trackHeight = appearance?.trackThickness ?? barHeight;

  const trackStyle: CSSProperties = {
    ...(appearance?.trackColor && { backgroundColor: appearance.trackColor }),
    height: trackHeight,
    borderRadius: trackHeight / 2,
  };

  const fillStyle: CSSProperties = {
    width: `${progress}%`,
    ...(appearance?.fillColor && { backgroundColor: appearance.fillColor }),
    ...(appearance?.color && !appearance?.fillColor && { backgroundColor: appearance.color }),
    height: barHeight,
  };

  const image = currentSegment?.content?.image;

  const itemEditPath = editPath && currentSegmentIndex >= 0
    ? `${editPath}.items.${currentSegmentIndex}.content`
    : undefined;

  return (
    <div
      className={className ? `${styles.container} ${className}` : styles.container}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div
        className={styles.progressBar}
        style={{ ...trackStyle, ...containerStyle }}
      >
        <div className={styles.fill} style={fillStyle} />
      </div>

      {currentSegment && (
        <div className={styles.items}>
          <div className={styles.item}>
            {image && (
              <Image
                image={image}
                locale={locale}
                className={styles.itemImage}
                previewMode={previewMode}
                fillContainer
                editPath={itemEditPath ? `${itemEditPath}.image` : undefined}
              />
            )}
            {currentSegment.content?.title && (
              <Text
                label={currentSegment.content.title}
                locale={locale}
                variables={variables}
                style={hasLabelStyle ? labelStyle : undefined}
                editPath={itemEditPath ? `${itemEditPath}.title` : undefined}
              />
            )}
            {currentSegment.content?.subtitle && (
              <Text
                label={currentSegment.content.subtitle}
                locale={locale}
                variables={variables}
                style={hasLabelStyle ? labelStyle : undefined}
                editPath={itemEditPath ? `${itemEditPath}.subtitle` : undefined}
              />
            )}
            {currentSegment.content?.description && (
              <Text
                label={currentSegment.content.description}
                locale={locale}
                variables={variables}
                style={hasLabelStyle ? labelStyle : undefined}
                className={styles.itemDescription}
                editPath={itemEditPath ? `${itemEditPath}.description` : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

LineProgressBar.displayName = 'LineProgressBar';
